import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { PromptEvaluation, PromptSuggestion, PromptAnalysis } from '../services/openRouterService';
import { useLanguage } from '../hooks/useLanguage';
import { MarkdownRenderer } from './MarkdownRenderer';

interface PromptPreviewProps {
  evaluation: PromptEvaluation | null;
  isLoading: boolean;
  error: string | null;
  onClose: () => void;
  onApplySuggestion?: (suggestion: PromptSuggestion) => void;
  onReanalyze?: () => void;
  currentContent?: string;
  evaluatedContent?: string;
}

export const PromptPreview: React.FC<PromptPreviewProps> = ({
  evaluation,
  isLoading,
  error,
  onClose,
  onApplySuggestion,
  onReanalyze,
  currentContent = '',
  evaluatedContent = ''
}) => {
  const [activeTab, setActiveTab] = useState<'analysis' | 'suggestions' | 'preview'>('analysis');
  const { isPortuguese } = useLanguage();
  const { t } = useTranslation();
  
  // Check if current content differs from evaluated content
  const contentChanged = currentContent.trim() !== evaluatedContent.trim();

  const getSeverityColor = (severity: 'low' | 'medium' | 'high') => {
    switch (severity) {
      case 'low': return 'text-blue-700 bg-blue-50 border-blue-200';
      case 'medium': return 'text-yellow-700 bg-yellow-50 border-yellow-200';
      case 'high': return 'text-red-700 bg-red-50 border-red-200';
    }
  };

  const getSuggestionIcon = (type: PromptSuggestion['type']) => {
    switch (type) {
      case 'clarity':
        return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z" />
        </svg>;
      case 'specificity':
        return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0zM10 7v3m0 0v3m0-3h3m-3 0H7" />
        </svg>;
      case 'structure':
        return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 10h16M4 14h16M4 18h16" />
        </svg>;
      case 'context':
        return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>;
      case 'examples':
        return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
        </svg>;
      case 'constraints':
        return <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4M7.835 4.697a3.42 3.42 0 001.946-.806 3.42 3.42 0 014.438 0 3.42 3.42 0 001.946.806 3.42 3.42 0 013.138 3.138 3.42 3.42 0 00.806 1.946 3.42 3.42 0 010 4.438 3.42 3.42 0 00-.806 1.946 3.42 3.42 0 01-3.138 3.138 3.42 3.42 0 00-1.946.806 3.42 3.42 0 01-4.438 0 3.42 3.42 0 00-1.946-.806 3.42 3.42 0 01-3.138-3.138 3.42 3.42 0 00-.806-1.946 3.42 3.42 0 010-4.438 3.42 3.42 0 00.806-1.946 3.42 3.42 0 013.138-3.138z" />
        </svg>;
    }
  };

  const ScoreBar: React.FC<{ label: string; value: number; max?: number }> = ({ 
    label, 
    value, 
    max = 100 
  }) => {
    const percentage = Math.min((value / max) * 100, 100);
    const getColor = (score: number) => {
      if (score >= 80) return 'bg-green-500';
      if (score >= 60) return 'bg-yellow-500';
      if (score >= 40) return 'bg-orange-500';
      return 'bg-red-500';
    };

    return (
      <div className="mb-3">
        <div className="flex justify-between text-sm mb-1">
          <span className="text-ghost-700 font-medium">{label}</span>
          <span className="text-ghost-600">{value}{max === 100 ? '/100' : ''}</span>
        </div>
        <div className="w-full bg-ghost-200 rounded-full h-2">
          <div 
            className={`h-2 rounded-full transition-all duration-500 ${getColor(percentage)}`}
            style={{ width: `${percentage}%` }}
          />
        </div>
      </div>
    );
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden flex flex-col">
        {/* Header */}
        <div className="p-6 border-b border-ghost-200 flex-shrink-0">
          <div className="flex items-start justify-between">
            <div className="flex-1">
              <div className="flex items-center gap-3">
                <div>
                  <h2 className="text-xl font-semibold text-ghost-800">
                    {t('promptPreview.title')}
                  </h2>
                  <p className="text-sm text-ghost-600 mt-1">
                    {t('promptPreview.subtitle')}
                  </p>
                </div>
                
                {/* Content change indicator and re-analyze button */}
                {contentChanged && onReanalyze && (
                  <div className="flex items-center gap-2 ml-4">
                    <div className="flex items-center gap-2 px-3 py-1 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <svg className="w-4 h-4 text-yellow-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z" />
                      </svg>
                      <span className="text-xs text-yellow-700">
                        {t('promptPreview.textChanged')}
                      </span>
                    </div>
                    <button
                      onClick={onReanalyze}
                      className="px-3 py-1 bg-blue-600 text-white text-xs rounded-lg hover:bg-blue-700 transition-colors"
                    >
                      {t('promptPreview.reanalyze')}
                    </button>
                  </div>
                )}
              </div>
            </div>
            
            <button
              onClick={onClose}
              className="text-ghost-400 hover:text-ghost-600 transition-colors p-1 flex-shrink-0"
            >
              <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* Tabs */}
          <div className="flex space-x-1 mt-4 bg-ghost-100 rounded-lg p-1">
            {[
              { id: 'analysis', label: t('promptPreview.tabs.analysis'), icon: '📊' },
              { id: 'suggestions', label: t('promptPreview.tabs.suggestions'), icon: '💡' },
              { id: 'preview', label: t('promptPreview.tabs.preview'), icon: '👁️' }
            ].map((tab) => (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id as any)}
                className={`flex-1 px-3 py-2 text-sm font-medium rounded-md transition-colors ${
                  activeTab === tab.id
                    ? 'bg-white text-ghost-900 shadow-sm'
                    : 'text-ghost-600 hover:text-ghost-900'
                }`}
              >
                <span className="mr-2">{tab.icon}</span>
                {tab.label}
              </button>
            ))}
          </div>
        </div>

        {/* Content */}
        <div className="flex-1 overflow-y-auto p-6">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin w-8 h-8 border-4 border-blue-600 border-t-transparent rounded-full mx-auto mb-4"></div>
                <p className="text-ghost-600">{t('promptPreview.analyzing')}</p>
              </div>
            </div>
          )}

          {error && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <div className="flex items-center">
                <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                <span className="text-red-700">{error}</span>
              </div>
            </div>
          )}

          {evaluation && (
            <>
              {/* Analysis Tab */}
              {activeTab === 'analysis' && (
                <div className="space-y-6">
                  {/* Overall Score */}
                  <div className="bg-gradient-to-r from-blue-50 to-purple-50 rounded-lg p-6 border border-blue-100">
                    <div className="text-center">
                      <div className="text-4xl font-bold text-ghost-800 mb-2">
                        {evaluation.analysis.overallScore}/100
                      </div>
                      <p className="text-ghost-600">{t('promptPreview.overallScore')}</p>
                    </div>
                  </div>

                  {/* Detailed Metrics */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                    <div className="space-y-4">
                      <h3 className="font-semibold text-ghost-800 mb-4">{t('promptPreview.qualityMetrics')}</h3>
                      <ScoreBar label={t('promptPreview.metrics.clarity')} value={evaluation.analysis.clarity} />
                      <ScoreBar label={t('promptPreview.metrics.specificity')} value={evaluation.analysis.specificity} />
                      <ScoreBar label={t('promptPreview.metrics.structure')} value={evaluation.analysis.structure} />
                      <ScoreBar label={t('promptPreview.metrics.context')} value={evaluation.analysis.context} />
                    </div>

                    <div className="space-y-4">
                      <h3 className="font-semibold text-ghost-800 mb-4">{t('promptPreview.statistics')}</h3>
                      <ScoreBar label={t('promptPreview.metrics.readability')} value={evaluation.analysis.readabilityScore} />
                      <ScoreBar label={t('promptPreview.metrics.words')} value={evaluation.analysis.wordCount} max={500} />
                      <ScoreBar label={t('promptPreview.metrics.tokens')} value={evaluation.analysis.tokenEstimate} max={2000} />
                      
                      <div className="mt-4 space-y-2">
                        <div className="flex justify-between text-sm">
                          <span>Exemplos incluídos:</span>
                          <span className={evaluation.analysis.examples ? 'text-green-600' : 'text-red-600'}>
                            {evaluation.analysis.examples ? '✅ Sim' : '❌ Não'}
                          </span>
                        </div>
                        <div className="flex justify-between text-sm">
                          <span>Restrições definidas:</span>
                          <span className={evaluation.analysis.constraints ? 'text-green-600' : 'text-red-600'}>
                            {evaluation.analysis.constraints ? '✅ Sim' : '❌ Não'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>

                  {/* Usage Stats */}
                  {evaluation.usage.totalTokens > 0 && (
                    <div className="bg-ghost-50 rounded-lg p-4">
                      <h3 className="font-semibold text-ghost-800 mb-3">{t('promptPreview.apiUsage')}</h3>
                      <div className="grid grid-cols-3 gap-4 text-center text-sm">
                        <div>
                          <div className="font-medium text-ghost-800">{evaluation.usage.promptTokens}</div>
                          <div className="text-ghost-600">{t('promptPreview.usage.promptTokens')}</div>
                        </div>
                        <div>
                          <div className="font-medium text-ghost-800">{evaluation.usage.completionTokens}</div>
                          <div className="text-ghost-600">{t('promptPreview.usage.completionTokens')}</div>
                        </div>
                        <div>
                          <div className="font-medium text-ghost-800">{evaluation.usage.totalTokens}</div>
                          <div className="text-ghost-600">{t('promptPreview.usage.total')}</div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              )}

              {/* Suggestions Tab */}
              {activeTab === 'suggestions' && (
                <div className="space-y-4">
                  {evaluation.suggestions.length === 0 ? (
                    <div className="text-center py-12">
                      <div className="text-6xl mb-4">🎉</div>
                      <h3 className="text-xl font-semibold text-ghost-800 mb-2">{t('promptPreview.suggestions.excellentPrompt')}</h3>
                      <p className="text-ghost-600">{t('promptPreview.suggestions.noSuggestions')}</p>
                    </div>
                  ) : (
                    <>
                      <div className="mb-6">
                        <h3 className="font-semibold text-ghost-800">
                          {evaluation.suggestions.length} {t('promptPreview.suggestions.improvementSuggestions')}
                        </h3>
                        <p className="text-sm text-ghost-600 mt-1">
                          {t('promptPreview.suggestions.clickToApply')}
                        </p>
                      </div>

                      {evaluation.suggestions.map((suggestion, index) => (
                        <div
                          key={index}
                          className={`border rounded-lg p-4 ${getSeverityColor(suggestion.severity)}`}
                        >
                          <div className="flex items-start justify-between">
                            <div className="flex items-start space-x-3 flex-1">
                              <div className="flex-shrink-0 mt-0.5">
                                {getSuggestionIcon(suggestion.type)}
                              </div>
                              <div className="flex-1">
                                <div className="flex items-center space-x-2 mb-2">
                                  <h4 className="font-semibold">{suggestion.title}</h4>
                                  <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                                    suggestion.severity === 'high' 
                                      ? 'bg-red-100 text-red-700'
                                      : suggestion.severity === 'medium'
                                      ? 'bg-yellow-100 text-yellow-700'
                                      : 'bg-blue-100 text-blue-700'
                                  }`}>
                                    {suggestion.severity === 'high' ? t('promptPreview.suggestions.severity.high') : 
                                     suggestion.severity === 'medium' ? t('promptPreview.suggestions.severity.medium') : t('promptPreview.suggestions.severity.low')}
                                  </span>
                                </div>
                                <p className="text-sm mb-3">{suggestion.description}</p>
                                {suggestion.example && (
                                  <div className="bg-white/50 rounded p-3 text-sm">
                                    <div className="font-medium mb-1">{t('promptPreview.suggestions.example')}</div>
                                    <code className="text-xs">{suggestion.example}</code>
                                  </div>
                                )}
                                <p className="text-xs mt-2 opacity-80">
                                  <strong>{t('promptPreview.suggestions.reasoning')}</strong> {suggestion.reasoning}
                                </p>
                              </div>
                            </div>
                            {onApplySuggestion && (
                              <button
                                onClick={() => onApplySuggestion(suggestion)}
                                className="ml-4 px-3 py-1 bg-white/70 hover:bg-white transition-colors rounded text-xs font-medium"
                              >
                                {t('promptPreview.suggestions.apply')}
                              </button>
                            )}
                          </div>
                        </div>
                      ))}
                    </>
                  )}
                </div>
              )}

              {/* Preview Tab */}
              {activeTab === 'preview' && (
                <div className="space-y-4">
                  <div className="flex items-center justify-between">
                    <div>
                      <h3 className="font-semibold text-ghost-800 mb-1">{t('promptPreview.preview.exampleResponse')}</h3>
                      <p className="text-sm text-ghost-600">
                        {t('promptPreview.preview.description')}
                      </p>
                    </div>
                    
                    {/* Copy button */}
                    <button
                      onClick={() => {
                        navigator.clipboard.writeText(evaluation.response);
                        // Could add a toast notification here
                      }}
                      className="flex items-center gap-2 px-3 py-2 text-sm text-ghost-600 hover:text-ghost-800 bg-white border border-ghost-200 hover:border-ghost-300 rounded-lg transition-colors"
                    >
                      <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 16H6a2 2 0 01-2-2V6a2 2 0 012-2h8a2 2 0 012 2v2m-6 12h8a2 2 0 002-2v-8a2 2 0 00-2-2h-8a2 2 0 00-2 2v8a2 2 0 002 2z" />
                      </svg>
                      {isPortuguese ? 'Copiar' : 'Copy'}
                    </button>
                  </div>
                  
                  <div className="bg-white rounded-lg p-4 border border-ghost-200">
                    <MarkdownRenderer content={evaluation.response} />
                  </div>
                  
                  <div className="text-xs text-ghost-500 text-center bg-ghost-50 rounded-lg p-3">
                    <div className="flex items-center justify-center gap-2">
                      <svg className="w-4 h-4 text-ghost-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 7h6m0 10v-3m-3 3h.01M9 17h.01M9 14h.01M12 14h.01M15 11h.01M12 11h.01M9 11h.01M7 21h10a2 2 0 002-2V5a2 2 0 00-2-2H7a2 2 0 00-2 2v14a2 2 0 002 2z" />
                      </svg>
                      {isPortuguese 
                        ? `Resposta gerada com ${evaluation.analysis.tokenEstimate} tokens estimados`
                        : `Response generated with ${evaluation.analysis.tokenEstimate} estimated tokens`
                      }
                    </div>
                  </div>
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};